import React, { useMemo, useState } from 'react';
import { Message, SelectPicker, toaster } from 'rsuite';
import { Button as NsiButton, Modal } from '@nsi-advisory-services/components';

import {
  useActiveProvider,
  useProjectTimesheet,
  useRuntimeFeatureFlags,
  useTimesheetLink,
  useTimesheetLinkCandidates,
} from '../hooks/useQueries';
import './TimesheetLink.css';

// Links a project to an EmployeeConnect timesheet project, and its task orders to child tasks under
// that project, so EmployeeConnect's timesheet reports can price the hours at this project's rates.
// The link is stored in EmployeeConnect; everything here goes through ProPlan's server.

const notify = (type, text) => {
  toaster.push(
    <Message type={type} showIcon closable>
      {text}
    </Message>,
    { placement: 'topEnd', duration: type === 'error' ? 6000 : 2500 }
  );
};

// Hidden while the flags load, when Connect Suite is off, and when hours come from somewhere other
// than EmployeeConnect. Without the flag (an error, or an older server) the timesheet is still tried,
// and the server says what is wrong.
export const useTimesheetAvailability = () => {
  const { data, isLoading } = useRuntimeFeatureFlags();
  const provider = useActiveProvider();
  return {
    loading: isLoading || provider.isLoading,
    enabled:
      data?.hasConnectSuite !== false && String(provider.data?.name || '').toLowerCase() === 'employeeconnect',
    configured: data?.hasEmployeeConnect !== false,
  };
};

const useTimesheetStatus = (projectId) => {
  const availability = useTimesheetAvailability();
  const status = useProjectTimesheet(projectId, {
    enabled: !availability.loading && availability.enabled && availability.configured,
  });
  return { availability, status };
};

// Searchable list of what can be linked: unlinked EmployeeConnect projects, or with a taskOrderId the
// unlinked tasks under the project's linked EmployeeConnect project.
function TimesheetLinkPicker({ open, onClose, projectId, taskOrderId = null, title, intro, onLink, linking }) {
  const [value, setValue] = useState(null);
  const candidates = useTimesheetLinkCandidates({ projectId, taskOrderId }, { enabled: open });

  const data = useMemo(
    () =>
      (Array.isArray(candidates.data) ? candidates.data : []).map((c) => ({
        value: c.id,
        label: taskOrderId ? c.path || c.name : c.clientName ? `${c.name} · ${c.clientName}` : c.name,
      })),
    [candidates.data, taskOrderId]
  );

  const close = () => {
    if (linking) return;
    onClose();
  };

  let body;
  if (candidates.isLoading) {
    body = <p className="ppc-ts-note">Loading from EmployeeConnect…</p>;
  } else if (candidates.error) {
    body = (
      <Message type="error" showIcon>
        {String(candidates.error?.message || 'Could not load from EmployeeConnect.')}
      </Message>
    );
  } else if (data.length === 0) {
    body = (
      <Message type="info" showIcon>
        {taskOrderId
          ? 'Every task under the linked timesheet project is already linked. Use "Add to timesheet" to create a new one.'
          : 'Every EmployeeConnect timesheet project is already linked. Use "Add to timesheet" to create a new one.'}
      </Message>
    );
  } else {
    body = (
      <SelectPicker
        block
        searchable
        data={data}
        value={value}
        onChange={setValue}
        placeholder={taskOrderId ? 'Search timesheet tasks' : 'Search timesheet projects'}
        disabled={linking}
        menuStyle={{ zIndex: 1100 }}
      />
    );
  }

  return (
    <Modal open={open} onClose={close} onExited={() => setValue(null)} size="sm">
      <Modal.Header>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="ppc-ts-picker">
          <p className="ppc-ts-note">{intro}</p>
          {body}
        </div>
      </Modal.Body>
      <Modal.Footer>
        <NsiButton onClick={close} disabled={linking}>
          Cancel
        </NsiButton>
        <NsiButton
          variant="primary"
          disabled={!value}
          loading={linking}
          onClick={() => onLink(value, () => setValue(null))}
        >
          Link
        </NsiButton>
      </Modal.Footer>
    </Modal>
  );
}

// The Timesheet section on the project page.
// canEdit false (view-only on the project): the status shows, the link buttons don't.
export function ProjectTimesheetSection({ projectId, canEdit = true }) {
  const { availability, status } = useTimesheetStatus(projectId);
  const link = useTimesheetLink();
  const [pickerOpen, setPickerOpen] = useState(false);
  const [confirmUnlinkOpen, setConfirmUnlinkOpen] = useState(false);

  if (availability.loading || !availability.enabled) return null;

  const linked = status.data?.project ?? null;
  const linkedTaskOrderCount = Array.isArray(status.data?.taskOrders) ? status.data.taskOrders.length : 0;

  const run = (variables, successText, after) => {
    link.mutate(
      { projectId, ...variables },
      {
        onSuccess: () => {
          notify('success', successText);
          after?.();
        },
        onError: (err) => notify('error', String(err?.message || 'EmployeeConnect request failed.')),
      }
    );
  };

  let body;
  if (!availability.configured) {
    body = (
      <Message type="info" showIcon>
        EmployeeConnect isn&apos;t set up on this server, so this project can&apos;t be added to the timesheet.
        An administrator needs to configure the EmployeeConnect connection.
      </Message>
    );
  } else if (status.isLoading) {
    body = <p className="ppc-ts-note">Checking the EmployeeConnect timesheet…</p>;
  } else if (status.error) {
    body = (
      <Message type="warning" showIcon>
        <div className="ppc-ts-error">
          <span>
            Couldn&apos;t reach the EmployeeConnect timesheet: {String(status.error?.message || 'unknown error')}
          </span>
          <button type="button" className="ppc-pd-btn ppc-pd-btn--sm" onClick={() => status.refetch()}>
            Try again
          </button>
        </div>
      </Message>
    );
  } else if (linked) {
    body = (
      <div className="ppc-ts-row">
        <div className="ppc-ts-row__text">
          <span className="ppc-ts-linked">
            <i className="bi bi-link-45deg" aria-hidden="true" />
            On the timesheet as <strong>{linked.name || linked.id}</strong>
            {linked.archived ? <span className="ppc-ts-tag">Archived</span> : null}
          </span>
          <p className="ppc-ts-note">
            Time logged against it in EmployeeConnect is reported with this project&apos;s rates.
          </p>
        </div>
        {canEdit ? (
          <button
            type="button"
            className="ppc-pd-btn ppc-pd-btn--sm"
            onClick={() => setConfirmUnlinkOpen(true)}
            disabled={link.isPending}
          >
            <i className="bi bi-x-lg" aria-hidden="true" />
            Unlink
          </button>
        ) : null}
      </div>
    );
  } else if (!canEdit) {
    body = <p className="ppc-ts-note">Not on the EmployeeConnect timesheet yet.</p>;
  } else {
    body = (
      <div className="ppc-ts-row">
        <p className="ppc-ts-note">Not on the EmployeeConnect timesheet yet.</p>
        <div className="ppc-ts-actions">
          <button
            type="button"
            className="ppc-pd-btn ppc-pd-btn--sm ppc-pd-btn--primary"
            onClick={() => run({ action: 'create' }, 'Added to the timesheet.')}
            disabled={link.isPending}
            title="Create an EmployeeConnect timesheet project named after this project"
          >
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add to timesheet
          </button>
          <button
            type="button"
            className="ppc-pd-btn ppc-pd-btn--sm"
            onClick={() => setPickerOpen(true)}
            disabled={link.isPending}
          >
            <i className="bi bi-link-45deg" aria-hidden="true" />
            Link existing
          </button>
        </div>
      </div>
    );
  }

  return (
    <section className="ppc-pd-section">
      <div className="ppc-pd-section__head">
        <h3 className="ppc-pd-section__title">
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <i className="bi bi-clock-history" aria-hidden="true" />
            Timesheet
          </span>
        </h3>
      </div>

      {body}

      <TimesheetLinkPicker
        open={pickerOpen}
        onClose={() => setPickerOpen(false)}
        projectId={projectId}
        title="Link to a timesheet project"
        intro="Pick the EmployeeConnect timesheet project this project's time is logged against."
        linking={link.isPending}
        onLink={(emsId, reset) =>
          run({ action: 'link', emsId }, 'Linked to the timesheet.', () => {
            reset();
            setPickerOpen(false);
          })
        }
      />

      <Modal open={confirmUnlinkOpen} onClose={() => !link.isPending && setConfirmUnlinkOpen(false)} size="xs">
        <Modal.Header>
          <Modal.Title>Unlink from the timesheet?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="ppc-ts-note">
            This project will no longer be linked to <strong>{linked?.name}</strong>
            {linkedTaskOrderCount > 0
              ? `, and its ${linkedTaskOrderCount} linked task order${linkedTaskOrderCount === 1 ? '' : 's'} will be unlinked too`
              : ''}
            . Nothing is deleted in EmployeeConnect; time already entered stays there.
          </p>
        </Modal.Body>
        <Modal.Footer>
          <NsiButton onClick={() => setConfirmUnlinkOpen(false)} disabled={link.isPending}>
            Cancel
          </NsiButton>
          <NsiButton
            variant="danger-solid"
            loading={link.isPending}
            onClick={() => run({ action: 'unlink' }, 'Unlinked from the timesheet.', () => setConfirmUnlinkOpen(false))}
          >
            Unlink
          </NsiButton>
        </Modal.Footer>
      </Modal>
    </section>
  );
}

// The timesheet line on a task order card: add or link a child task under the project's linked
// EmployeeConnect project. Problems reaching EmployeeConnect show once, in the project's section.
export function TaskOrderTimesheetControl({ projectId, taskOrder, canEdit = true }) {
  const { availability, status } = useTimesheetStatus(projectId);
  const link = useTimesheetLink();
  const [pickerOpen, setPickerOpen] = useState(false);

  const taskOrderId = Number(taskOrder?.id);
  if (availability.loading || !availability.enabled || !availability.configured) return null;
  if (!Number.isInteger(taskOrderId) || !status.data) return null;

  const projectLinked = Boolean(status.data.project);
  const linkedTask = (status.data.taskOrders || []).find((t) => Number(t?.taskOrderId) === taskOrderId)?.task ?? null;
  const name = String(taskOrder?.name || 'Task order');
  if (!canEdit && !linkedTask) return null;

  const run = (variables, successText, after) => {
    link.mutate(
      { projectId, taskOrderId, ...variables },
      {
        onSuccess: () => {
          notify('success', successText);
          after?.();
        },
        onError: (err) => notify('error', String(err?.message || 'EmployeeConnect request failed.')),
      }
    );
  };

  // The card navigates on click; keep clicks in here (and in the picker portal's React tree) to itself.
  const stop = (e) => e.stopPropagation();

  let content;
  if (linkedTask) {
    content = (
      <>
        <span className="ppc-ts-linked ppc-ts-linked--sm" title={linkedTask.path}>
          <i className="bi bi-clock-history" aria-hidden="true" />
          <span className="ppc-ts-linked__name">{linkedTask.path || linkedTask.name}</span>
        </span>
        {canEdit ? <button
          type="button"
          className="ppc-ts-iconbtn"
          title={`Unlink ${name} from the timesheet`}
          aria-label={`Unlink ${name} from the timesheet`}
          onClick={() => run({ action: 'unlink' }, `${name} unlinked from the timesheet.`)}
          disabled={link.isPending}
        >
          <i className="bi bi-x-lg" aria-hidden="true" />
        </button> : null}
      </>
    );
  } else {
    content = (
      <>
        <div className="ppc-ts-actions">
          <button
            type="button"
            className="ppc-pd-btn ppc-pd-btn--sm"
            onClick={() => run({ action: 'create' }, `${name} added to the timesheet.`)}
            disabled={!projectLinked || link.isPending}
            title={projectLinked ? `Create a timesheet task for ${name} under ${status.data.project.name}` : undefined}
          >
            <i className="bi bi-plus-lg" aria-hidden="true" />
            Add to timesheet
          </button>
          <button
            type="button"
            className="ppc-pd-btn ppc-pd-btn--sm"
            onClick={() => setPickerOpen(true)}
            disabled={!projectLinked || link.isPending}
          >
            <i className="bi bi-link-45deg" aria-hidden="true" />
            Link existing
          </button>
        </div>
        {!projectLinked ? (
          <p className="ppc-ts-note ppc-ts-note--sm">Link the project to the timesheet first.</p>
        ) : null}
      </>
    );
  }

  return (
    <div className={`ppc-ts-to${linkedTask ? ' ppc-ts-to--linked' : ''}`} onClick={stop} onKeyDown={stop} role="presentation">
      {content}
      {projectLinked ? (
        <TimesheetLinkPicker
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          projectId={projectId}
          taskOrderId={taskOrderId}
          title={`Link ${name} to a timesheet task`}
          intro={`Pick the task under ${status.data.project.name} that ${name}'s time is logged against.`}
          linking={link.isPending}
          onLink={(emsId, reset) =>
            run({ action: 'link', emsId }, `${name} linked to the timesheet.`, () => {
              reset();
              setPickerOpen(false);
            })
          }
        />
      ) : null}
    </div>
  );
}
